/* eslint-disable no-unused-vars */
const Types = require('../../../../jsdoc-types');
/* eslint-enable no-unused-vars */

const { getPageNameFromProductTileLocation } = require('./analyticUtils');
const { analyticsEventBus, ANALYTICS_EVENTS } = require('./events');
const createDebouncedEventBatchHandler = require('../common/utils/createDebouncedEventBatchHandler');

/**
 * Register a click on an Einstein recommendation.
 * @param {string} productID - The Product ID
 * @param {string} pageName - The page name where the product was clicked
 * @param {string} [recommenderName] - The name of the recommender
 */
function registerClickRecommendation(productID, pageName, recommenderName) {
  const { default: getFrontConfig } = require('../common/getFrontConfig');
  const FRONT_CONFIG = getFrontConfig();

  const url = `${FRONT_CONFIG.urls.registerClickRecommendation}?data=${encodeURIComponent(
    JSON.stringify({
      pageName,
      productID,
      recommenderName,
    })
  )}`;
  fetch(url, {
    method: 'GET',
    keepalive: true,
  })
    .then((response) => response.json())
    .then((data) => {
      if (!data.success) {
        console.error('Error registering Einstein recommendation click:', data);
      }
    })
    .catch((error) => {
      console.error('Error registering Einstein recommendation click:', error);
    });
}

/**
 * Handle product tile clicked event for Einstein Analytics.
 * @param {Object} payload The payload of the event
 * @param {Types.Category} [payload.category] If the location is a PLP Grid,
 *  the category of the ProductSearch can be sent
 * @param {string} payload.location The location where the tile is clicked
 * @param {string} [payload.position] The product position when applies
 * @param {Types.FullProduct} payload.product Target product
 * @param {Object} [payload.recommender] Recommender info
 * @param {Object} [payload.recommender.name] Recommender name
 * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
 */
function handleProductTileClicked({ location, product, recommender }) {
  if (!recommender?.isEinstein || !location || !product) {
    return;
  }

  const pageName = getPageNameFromProductTileLocation(location);
  registerClickRecommendation(product.id, pageName);
}

/**
 * Handle product tile quickshop clicked event for Einstein Analytics.
 * @param {Object} payload - The payload of the event
 * @param {string} payload.location - The location where the tile is clicked
 * @param {string} payload.productId - The product SKU
 * @param {Object} [payload.recommender] Recommender info
 * @param {Object} [payload.recommender.name] Recommender name
 * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
 */
function handleProductTileQuickshopClicked({
  location,
  productId,
  recommender,
}) {
  if (!recommender?.isEinstein || !location || !productId) {
    return;
  }
  const pageName = getPageNameFromProductTileLocation(location);
  registerClickRecommendation(productId, pageName);
}

/**
 * Handle search recommended product clicked event for Einstein Analytics.
 * @param {Object} payload - The payload of the event
 * @param {string} payload.location - The location where the tile is clicked
 * @param {string} payload.productId - The product SKU
 * @param {Object} [payload.recommender] Recommender info
 * @param {Object} [payload.recommender.name] Recommender name
 * @param {Object} [payload.recommender.isEinstein=false] True if the recommender is from Einstein
 */
function handleSearchRecommendedProductClicked({
  location,
  productId,
  recommender,
}) {
  if (!recommender?.isEinstein || !location || !productId) {
    return;
  }
  const pageName = getPageNameFromProductTileLocation(location);
  registerClickRecommendation(productId, pageName, recommender.name);
}

/**
 * Register a view on an Einstein recommendation.
 * @param {string} productID - The Product ID
 * @param {string} pageName - The page name where the product was viewed
 * @param {string} [recommenderName] - The name of the recommender
 */
function registerViewRecommendation(productID, pageName, recommenderName) {
  const { default: getFrontConfig } = require('../common/getFrontConfig');
  const FRONT_CONFIG = getFrontConfig();

  const url = `${FRONT_CONFIG.urls.registerViewRecommendation}?data=${encodeURIComponent(
    JSON.stringify({
      pageName,
      productIDs: [productID],
      recommenderName,
    })
  )}`;
  fetch(url, {
    method: 'GET',
    keepalive: true,
  })
    .then((response) => response.json())
    .then((data) => {
      if (!data.success) {
        console.error('Error registering Einstein recommendation view:', data);
      }
    })
    .catch((error) => {
      console.error('Error registering Einstein recommendation view:', error);
    });
}

/**
 * Initialize Einstein Analytics tracking.
 */
function init() {
  analyticsEventBus.subscribe(
    ANALYTICS_EVENTS.productTileClicked,
    handleProductTileClicked
  );

  analyticsEventBus.subscribe(
    ANALYTICS_EVENTS.productTileQuickshopClicked,
    handleProductTileQuickshopClicked
  );

  analyticsEventBus.subscribe(
    ANALYTICS_EVENTS.searchRecommendedProductClicked,
    handleSearchRecommendedProductClicked
  );

  analyticsEventBus.subscribe(
    ANALYTICS_EVENTS.productImpressionViewed,
    createDebouncedEventBatchHandler((payloads) => {
      const einsteinPayloads = payloads.filter(
        ({ recommender }) => recommender?.isEinstein
      );
      if (einsteinPayloads.length === 0) {
        return;
      }
      const productIDs = einsteinPayloads.map(({ product }) => product.id);
      if (productIDs.length === 0) {
        return;
      }
      const pageName = getPageNameFromProductTileLocation(
        einsteinPayloads[0].location
      );
      registerViewRecommendation(productIDs, pageName);
    })
  );

  analyticsEventBus.subscribe(
    ANALYTICS_EVENTS.searchRecommendedProductViewed,
    createDebouncedEventBatchHandler((payloads) => {
      const einsteinPayloads = payloads.filter(
        ({ recommender }) => recommender?.isEinstein
      );
      if (einsteinPayloads.length === 0) {
        return;
      }
      const productIds = einsteinPayloads.map(({ productId }) => productId);
      if (productIds.length === 0) {
        return;
      }
      const pageName = getPageNameFromProductTileLocation(
        einsteinPayloads[0].location
      );
      registerViewRecommendation(
        productIds,
        pageName,
        einsteinPayloads[0].recommender.name
      );
    })
  );
}

module.exports = {
  init,
};
